export class Pool<T extends new () => InstanceType<T> = new () => any> {
  private readonly _classType: T;
  private readonly _pool: InstanceType<T>[] = [];
  private _count = 0;
  private readonly _debug = false;

  /**
   * a pool of reusable instances of a class
   * @param ClassType -
   */
  constructor(ClassType: T, initialSize?: number) {
    this._classType = ClassType;

    if (initialSize) {
      this.prepopulate(initialSize);
    }
  }

  /**
   * create items to populate pool
   * @param total - the number of items to add to the pool
   */
  public prepopulate(total: number): void {
    for (let i = 0; i < total; i++) {
      this._pool.push(new this._classType());
    }
    this._count += total;
  }

  /** get an item from the pool, or create a new one if the pool is empty */
  public get(): InstanceType<T> {
    // grow the list by 20%ish if we're out
    if (this._pool.length <= 0) {
      this.prepopulate(Math.round(this._count * 0.2) + 1);
    }

    let item = this._pool.pop();

    if (!item) {
      item = new this._classType();
    }

    return item;
  }

  /**
   * add an object into the pool
   * @param item - the itm to add to the pool
   */
  public return(item: InstanceType<T>): void {
    if (this._pool.indexOf(item) !== -1) {
      if (this._debug) console.warn('Object already in pool', item);

      return;
    }

    this._pool.push(item);
  }

  public get totalSize(): number {
    return this._count;
  }

  public get totalFree(): number {
    return this._pool.length;
  }

  public get totalUsed(): number {
    return this._count - this._pool.length;
  }

  public clear() {
    this._pool.length = 0;
  }
}

export type PoolItem = {
  [x: string]: any;
  init?: (data?: any) => void;
  reset?: () => void;
};

export type PoolConstructor<K> = new () => K;
